// RegistrationFlyer.jsx
import React from "react"
import { motion } from "framer-motion"
import {
  Award,
  Users,
  CreditCard,
  Calendar,
  Hotel,
  Sparkles,
  BookOpen,
  Phone,
  Mail,
} from "lucide-react"
import { PhotoPlaceholder } from "../photo-placeholder"

/**
 * Note:
 * - Uses Tailwind classes. Adjust tokens (text-muted-foreground, primary, border, background)
 *   to match your design system or Tailwind config if needed.
 * - The flyer image provided by the system is referenced below as `footerImage`.
 *   If that path does not resolve in your environment, replace with an actual public path.
 */

const footerImage = "/mnt/data/77df4475-be5b-4886-a7c1-2ff1d874984a.png"

function THead({ children }) {
  return <th className="text-left px-3 py-2 font-semibold text-sm text-primary border-b border-border/60">{children}</th>
}
function TCell({ children }) {
  return <td className="px-3 py-2 text-sm text-muted-foreground">{children}</td>
}
function TableRow({ children }) {
  return <tr className="border-b border-border/60 last:border-b-0 hover:bg-muted/10 transition-colors">{children}</tr>
}

export function RegistrationSection() {
  return (
    <article className="max-w-7xl mx-auto p-6 space-y-8">
      {/* Award Banner */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="rounded-xl overflow-hidden shadow-lg border border-border/40"
      >
        <div className="bg-blue-400 text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Award className="w-7 h-7  text-white" />
              <div>
                <div className="text-xs text-muted-foreground">40th International Conference on</div>
                <h1 className="text-2xl font-bold leading-tight">Biomaterials — Bio-MANTHAN</h1>
              </div>
            </div>
          </div>

          <motion.div
            animate={{ scale: [1, 1.03, 1], rotate: [0, -1, 0] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md shadow-md"
          >
            <div className="text-sm">Awards of</div>
            <div className="text-lg font-bold">Rs. 2 Lakhs</div>
          </motion.div>
        </div>

        <div className="p-4 bg-white/90">
          <ul className="text-sm text-muted-foreground space-y-2">
            <li>✓ The American Chemical Society (ACS), Royal Society of Chemistry (RSC), and Bio-MANTHAN are offering a cash prize and a winning certificate for the <strong>Best 18 posters</strong> and <strong>Best 18 oral presentations</strong> worth Rs. 2 Lakhs.</li>
            <li>✓ Selected papers will be published in <strong>six peer-reviewed international journals</strong>.</li>
            <li>✓ Welcome kits + Gala Dinner + 4 days (Breakfast, High-tea, Lunch) + Participant’s certificate. <em>(For All)</em></li>
            <li>✓ Hostel Accommodation within the IIT campus <em>(Only for Students)</em></li>
          </ul>
        </div>
      </motion.header>

      {/* Main registration area (tables) */}
      <section className="grid gap-6 lg:grid-cols-3 ">
        {/* Left column: Conference Registration Fee (Offline) */}
        <AnimatedCard icon={Calendar} title="Conference Registration Fee (Offline)">
          <div className="overflow-x-auto ">
            <table className="w-full border-collapse">
              <thead className="bg-indigo-50">
                <TableRow>
                  <THead>Category</THead>
                  <THead>15 Oct’25</THead>
                  <THead>15 Nov’25</THead>
                </TableRow>
              </thead>
              <tbody>
                <TableRow>
                  <TCell>Student (member)</TCell>
                  <TCell>3500</TCell>
                  <TCell>4000</TCell>
                </TableRow>
                <TableRow>
                  <TCell>Student (non-member)</TCell>
                  <TCell>4500</TCell>
                  <TCell>5000</TCell>
                </TableRow>
                <TableRow>
                  <TCell>Faculty (member)</TCell>
                  <TCell>6000</TCell>
                  <TCell>6500</TCell>
                </TableRow>
                <TableRow>
                  <TCell>Faculty (non-member)</TCell>
                  <TCell>7000</TCell>
                  <TCell>7500</TCell>
                </TableRow>
                <TableRow>
                  <TCell>Accompanying (With Faculty)</TCell>
                  <TCell>4000</TCell>
                  <TCell>4500</TCell>
                </TableRow>
                <TableRow>
                  <TCell>Industry Delegate</TCell>
                  <TCell>10000</TCell>
                  <TCell>11000</TCell>
                </TableRow>
                <TableRow>
                  <TCell>Accompanying (With Industry Delegate)</TCell>
                  <TCell>6000</TCell>
                  <TCell>6500</TCell>
                </TableRow>

                {/* Foreign (USD) */}
                <tr className="bg-gray-50">
                  <td className="px-3 py-2 text-sm font-semibold" colSpan={3}>Foreign ($)</td>
                </tr>
                <TableRow>
                  <TCell>Student</TCell>
                  <TCell>250</TCell>
                  <TCell>300</TCell>
                </TableRow>
                <TableRow>
                  <TCell>Faculty</TCell>
                  <TCell>350</TCell>
                  <TCell>400</TCell>
                </TableRow>
                <TableRow>
                  <TCell>Accompanying person</TCell>
                  <TCell>250</TCell>
                  <TCell>300</TCell>
                </TableRow>

                {/* With accommodation (students) */}
                <tr className="bg-gray-50">
                  <td className="px-3 py-2 text-sm font-semibold" colSpan={3}>Indian (₹) with accommodation (students)</td>
                </tr>
                <TableRow>
                  <TCell>Student (member)</TCell>
                  <TCell>4500</TCell>
                  <TCell>5000</TCell>
                </TableRow>
                <TableRow>
                  <TCell>Student (non-member)</TCell>
                  <TCell>5500</TCell>
                  <TCell>6000</TCell>
                </TableRow>
              </tbody>
            </table>
          </div>
        </AnimatedCard>

        {/* Middle column: Conference Registration Fee (Online) */}
        <AnimatedCard icon={CreditCard} title="Conference Registration Fee (Online)">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-indigo-50">
                <TableRow>
                  <THead>Category</THead>
                  <THead>15 Oct’25</THead>
                  <THead>15 Nov’25</THead>
                </TableRow>
              </thead>
              <tbody>
                <TableRow>
                  <TCell>Student</TCell>
                  <TCell>2500</TCell>
                  <TCell>3000</TCell>
                </TableRow>
                <TableRow>
                  <TCell>Faculty</TCell>
                  <TCell>4500</TCell>
                  <TCell>5000</TCell>
                </TableRow>
                <TableRow>
                  <TCell>Industry Delegate</TCell>
                  <TCell>7000</TCell>
                  <TCell>7500</TCell>
                </TableRow>

                <tr className="bg-gray-50">
                  <td className="px-3 py-2 text-sm font-semibold" colSpan={3}>Foreign ($)</td>
                </tr>
                <TableRow>
                  <TCell>Student</TCell>
                  <TCell>150</TCell>
                  <TCell>200</TCell>
                </TableRow>
                <TableRow>
                  <TCell>Faculty</TCell>
                  <TCell>200</TCell>
                  <TCell>250</TCell>
                </TableRow>
              </tbody>
            </table>
          </div>
        </AnimatedCard>

        {/* Right column: Workshop & Hands-on Training */}
        <AnimatedCard icon={BookOpen} title="Workshop & Hands-on Training Charge">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-indigo-50">
                <TableRow>
                  <THead>Category</THead>
                  <THead>15 Oct’25</THead>
                  <THead>15 Nov’25</THead>
                </TableRow>
              </thead>
              <tbody>
                <TableRow>
                  <TCell>Student (member)</TCell>
                  <TCell>2000</TCell>
                  <TCell>2500</TCell>
                </TableRow>
                <TableRow>
                  <TCell>Student (non-member)</TCell>
                  <TCell>2500</TCell>
                  <TCell>3000</TCell>
                </TableRow>
                <TableRow>
                  <TCell>Faculty (member)</TCell>
                  <TCell>3000</TCell>
                  <TCell>3500</TCell>
                </TableRow>
                <TableRow>
                  <TCell>Faculty (non-member)</TCell>
                  <TCell>3500</TCell>
                  <TCell>4000</TCell>
                </TableRow>
                <TableRow>
                  <TCell>Industry Delegate</TCell>
                  <TCell>4500</TCell>
                  <TCell>5000</TCell>
                </TableRow>

                <tr className="bg-gray-50">
                  <td className="px-3 py-2 text-sm font-semibold" colSpan={3}>Indian (₹) with accommodation (students)</td>
                </tr>
                <TableRow>
                  <TCell>Student (member)</TCell>
                  <TCell>2500</TCell>
                  <TCell>3000</TCell>
                </TableRow>
                <TableRow>
                  <TCell>Student (non-member)</TCell>
                  <TCell>3000</TCell>
                  <TCell>3500</TCell>
                </TableRow>
              </tbody>
            </table>
          </div>
        </AnimatedCard>
      </section>

      {/* Group discounts (wide) */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="rounded-lg border p-4 bg-gradient-to-r from-indigo-50 to-purple-50 shadow-sm"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-primary/10">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <h3 className="font-semibold">Group discounts are available exclusively for students</h3>
          </div>
          <div className="text-sm text-muted-foreground">Group Size: <strong>5+ participants</strong> → <strong>10% on Total Payable Amount</strong></div>
        </div>

        <div className="mt-3 text-sm text-muted-foreground">
          <strong>Conditions:</strong> Must register together as a group (in a single payment). Only from the same institute are allowed.
        </div>
      </motion.div>

      {/* Inclusions & Awards + Accommodation */}
      <div className="grid gap-6 md:grid-cols-2">
        <InfoCard
          icon={Sparkles}
          title="Inclusions & Awards"
          items={[
            "Welcome kits, Gala Dinner, 4-day meals; Participant’s certificate",
            "Morning YOGA; Visit to the Archaeological Site Ropar",
            "Best 18 posters + Best 18 oral presentations worth Rs. 2 Lakhs (ACS, RSC & Bio-MANTHAN)",
            "Selected papers published in peer-reviewed international journals",
          ]}
        />

        <InfoCard
          icon={Hotel}
          title="Accommodation Package"
          items={[
            "3-Star SALT STAYZ, Mohali • Free transport Hotel ⇄ Venue",
            "Hostel Accommodation within IIT campus (Only for students) — limited",
            "GST: 18% extra",
          ]}
          extraLink={{
            href: "https://saltstayz.com/listing/saltstayz-trinity-mohali/",
            label: "Saltstayz Chandigarh Mohali",
          }}
        />
      </div>

      {/* Footer: Contact + image */}
      <motion.footer
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="rounded-lg overflow-hidden border shadow-sm"
      >
        <div className="p-4 bg-gradient-to-r from-sky-50 to-emerald-50">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="text-sm text-muted-foreground">BioMANTHAN 2025 • SBAOI & STERMI | Annual Meeting</div>
              <div className="mt-2 text-sm">
                Convener: <strong>Prof. Navin Kumar</strong> — Indian Institute of Technology (IIT) Ropar, Punjab, INDIA
              </div>
              <div className="mt-1 text-sm flex items-center gap-4 text-muted-foreground">
                <div className="flex items-center gap-1"><Phone className="w-4 h-4" /> +91 - (contact)</div>
                <div className="flex items-center gap-1"><Mail className="w-4 h-4" /> biomant han@iitrpr.ac.in</div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* footer left small logos could go here (placeholders) */}
              <div className="w-28 h-20 rounded-md overflow-hidden bg-white/60 border flex items-center justify-center text-xs text-muted-foreground">Institute logos</div>
              <div className="w-28 h-20 rounded-md overflow-hidden bg-white/60 border flex items-center justify-center text-xs text-muted-foreground">Sponsors</div>
            </div>
          </div>
        </div>

        {/* Large footer image (the one you uploaded) */}
        <div className="w-full">
          {/* if the image path is not publicly served in your setup, replace with <PhotoPlaceholder /> */}
          <img
            src={footerImage}
            alt="venue and saltstayz collage"
            className="w-full object-cover max-h-[260px] md:max-h-[300px]"
            onError={(e) => {
              // fallback to PhotoPlaceholder if the image path doesn't load
              e.currentTarget.style.display = "none"
            }}
          />
          <div className="p-3 bg-white/80 text-sm text-muted-foreground">
            <div>Note: All group members must register together to avail of this offer. Only from the same institute are allowed.</div>
            <div className="mt-1">* 18% GST Extra • # Accommodation in IIT Hostel (for students only)</div>
          </div>
        </div>
      </motion.footer>
    </article>
  )
}

/* ----------------------- Helper components ----------------------- */

function AnimatedCard({ children, title, icon: Icon }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.45 }}
      viewport={{ once: true }}
      className=" border border-gray-400 rounded-2xl bg-white/70 p-4 shadow-sm backdrop-blur-sm"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-md bg-primary/10">
            {Icon ? <Icon className="w-5 h-5 text-primary" /> : null}
          </div>
          <h3 className="font-semibold">{title}</h3>
        </div>
        <div className="text-xs text-muted-foreground">Indian (₹) / Foreign ($) as shown</div>
      </div>

      {children}
    </motion.div>
  )
}

function InfoCard({ icon: Icon, title, items = [], extraLink }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.01 }}
      transition={{ duration: 0.45 }}
      className="rounded-xl border bg-gradient-to-br from-muted/10 to-background/60 p-4 shadow-sm"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="p-2 rounded-md bg-primary/10">
          {Icon ? <Icon className="w-5 h-5 text-primary" /> : null}
        </div>
        <div>
          <h4 className="font-semibold">{title}</h4>
          <ul className="text-sm text-muted-foreground mt-2 space-y-1">
            {items.map((it, i) => (
              <li key={i} className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 mt-0.5 text-primary" /> <span>{it}</span>
              </li>
            ))}
          </ul>
          {extraLink && (
            <div className="mt-3">
              <a className="text-sm underline underline-offset-2 text-primary" href={extraLink.href} target="_blank" rel="noreferrer">
                {extraLink.label}
              </a>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  )
}
